import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import type { CentrifugeProgressMessage } from '@/hooks/useCentrifugeProgress';
import SocketProgressBar from '@/app/common/components/SocketProgressBar';
import SubtleBadge from '@/components/common/SubtleBadge';
import { getIndexerErrorInfo } from '../../api/errors';
import {
  useEnqueueIndexMutation,
  useLazyGetProjectJobQuery,
  usePatchOnboardingMutation,
} from '../../api/indexerApi';
import type { Project, ProjectJob } from '../../api/types';
import { isJobDone } from '../../lib/job-labels';
import { urlsFromProjectJob } from '../../lib/onboarding-job-urls';
import {
  inspectCompleteFromJob,
  parseJobProgressCompletion,
  type OnboardingInspectCompletePayload,
} from '../../lib/onboarding-inspect-complete';
import { formatFaNumber } from '../../lib/utils';
import { UrlListTextarea } from '../UrlListTextarea';

const INDEX_LIMIT = 200;
const FETCH_FALLBACK_ATTEMPTS = 3;
const FETCH_FALLBACK_MS = 800;
const PROGRESS_DONE_MESSAGE =
  'درخواست ایندکس تمام شد. در حال رفتن به مرحله بعد…';
const PROGRESS_DONE_FALLBACK_MESSAGE =
  'درخواست ایندکس تمام شد. برای رفتن به مرحله بعد روی «ادامه» بزنید.';

export function OnboardingIndexStep({
  project: _project,
  projectRef,
  inspectComplete,
  inspectJobId,
  indexJobId,
  onComplete,
}: {
  project: Project;
  projectRef: string;
  inspectComplete: OnboardingInspectCompletePayload | null;
  inspectJobId: string | null;
  indexJobId: string | null;
  onComplete: (submittedUrls: string[]) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [indexedCount, setIndexedCount] = useState(0);
  const [notIndexedCount, setNotIndexedCount] = useState(0);
  const [notIndexedUrls, setNotIndexedUrls] = useState<string[]>([]);
  const [job, setJob] = useState<ProjectJob | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [doneJobId, setDoneJobId] = useState<string | null>(null);
  const [progressDone, setProgressDone] = useState(false);
  const [message, setMessage] = useState<{
    variant: string;
    text: string;
  } | null>(null);
  const [skipped, setSkipped] = useState(false);
  const sentUrlsRef = useRef<string[]>([]);
  const completedRef = useRef(false);
  const completionMetaRef = useRef(parseJobProgressCompletion(null));
  const inspectInitializedRef = useRef(false);
  const [enqueueIndex] = useEnqueueIndexMutation();
  const [getProjectJob] = useLazyGetProjectJobQuery();
  const [patchOnboarding] = usePatchOnboardingMutation();

  const urlsToSend = useMemo(
    () => notIndexedUrls.slice(0, INDEX_LIMIT),
    [notIndexedUrls],
  );

  const urlListText = useMemo(() => urlsToSend.join('\n'), [urlsToSend]);

  const applyInspectPayload = useCallback(
    (payload: OnboardingInspectCompletePayload) => {
      setIndexedCount(payload.indexStatusSummary.indexed);
      setNotIndexedCount(payload.indexStatusSummary.notIndexed);
      setNotIndexedUrls(payload.notIndexedUrls);

      const allIndexed =
        isJobDone(payload.state) &&
        payload.indexStatusSummary.indexed > 0 &&
        payload.notIndexedUrls.length === 0;

      if (allIndexed) {
        setSkipped(true);
        void patchOnboarding({ projectId: projectRef, step: 5 })
          .unwrap()
          .then(() => onComplete([]));
      }
    },
    [projectRef, onComplete, patchOnboarding],
  );

  const markProgressDone = useCallback((jobId: string) => {
    setDoneJobId(jobId);
    setProgressDone(true);
    setActiveJobId(null);
    setMessage({
      variant: 'success',
      text: PROGRESS_DONE_MESSAGE,
    });
  }, []);

  const resolveIndexJobDone = useCallback(
    async (jobId: string): Promise<ProjectJob | null> => {
      const meta = completionMetaRef.current;
      if (meta?.state && isJobDone(meta.state)) {
        return getProjectJob({ projectId: projectRef, jobId }, true).unwrap();
      }

      for (let attempt = 0; attempt < FETCH_FALLBACK_ATTEMPTS; attempt++) {
        const loaded = await getProjectJob(
          { projectId: projectRef, jobId },
          true,
        ).unwrap();
        setJob(loaded);
        if (isJobDone(loaded.state)) return loaded;
        if (attempt < FETCH_FALLBACK_ATTEMPTS - 1) {
          await new Promise(resolve => setTimeout(resolve, FETCH_FALLBACK_MS));
        }
      }
      return null;
    },
    [projectRef, getProjectJob],
  );

  const advanceToNext = useCallback(
    async (jobId: string) => {
      if (completedRef.current) return;
      setFinishing(true);
      try {
        const loaded = await resolveIndexJobDone(jobId);
        if (!loaded || !isJobDone(loaded.state)) {
          setMessage({
            variant: 'success',
            text: PROGRESS_DONE_FALLBACK_MESSAGE,
          });
          return;
        }
        completedRef.current = true;
        const urls =
          sentUrlsRef.current.length > 0
            ? sentUrlsRef.current
            : urlsFromProjectJob(loaded);
        await patchOnboarding({
          projectId: projectRef,
          indexJobId: jobId,
          inspectJobId: inspectJobId ?? undefined,
          step: 5,
        }).unwrap();
        onComplete(urls);
      } catch {
        completedRef.current = false;
        setMessage({
          variant: 'warning',
          text: PROGRESS_DONE_FALLBACK_MESSAGE,
        });
      } finally {
        setFinishing(false);
      }
    },
    [
      projectRef,
      inspectJobId,
      onComplete,
      patchOnboarding,
      resolveIndexJobDone,
    ],
  );

  const finishIndexJob = useCallback(
    async (jobId: string) => {
      markProgressDone(jobId);
      await advanceToNext(jobId);
    },
    [markProgressDone, advanceToNext],
  );

  const handleSocketProgress = useCallback(
    (data: CentrifugeProgressMessage) => {
      const meta = parseJobProgressCompletion(data);
      if (meta) {
        completionMetaRef.current = meta;
      }
    },
    [],
  );

  useEffect(() => {
    if (!indexJobId || progressDone || completedRef.current) return;
    setActiveJobId(indexJobId);

    void getProjectJob({ projectId: projectRef, jobId: indexJobId }, true)
      .unwrap()
      .then(loaded => {
        if (progressDone || completedRef.current) return;
        setJob(loaded);
        if (isJobDone(loaded.state)) {
          completionMetaRef.current = {
            state: loaded.state,
            jobKind: 'google_index',
          };
          markProgressDone(indexJobId);
        }
      })
      .catch(() => {
        /* socket progress handles active jobs */
      });
  }, [indexJobId, projectRef, getProjectJob, progressDone, markProgressDone]);

  useEffect(() => {
    if (indexJobId) {
      setLoading(false);
      return;
    }
    if (inspectInitializedRef.current) return;

    if (inspectComplete) {
      inspectInitializedRef.current = true;
      applyInspectPayload(inspectComplete);
      setLoading(false);
      return;
    }

    if (!inspectJobId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    // Do not mark initialized until this attempt finishes successfully.
    // If the effect re-runs (unstable parent callbacks) and cancels the
    // in-flight request, a retry must still be allowed — otherwise loading
    // stays true forever ("در حال بارگذاری نتایج بازرسی…").

    void getProjectJob({ projectId: projectRef, jobId: inspectJobId }, true)
      .unwrap()
      .then(inspectJob => {
        if (cancelled) return;
        inspectInitializedRef.current = true;
        const payload = inspectCompleteFromJob(inspectJob);
        if (payload) {
          applyInspectPayload(payload);
        } else {
          setMessage({
            variant: 'warning',
            text: 'نتیجه‌ای از بازرسی دریافت نشد. در صورت نیاز «رد کردن» را بزنید.',
          });
        }
      })
      .catch(err => {
        if (cancelled) return;
        inspectInitializedRef.current = true;
        setMessage({
          variant: 'danger',
          text:
            getIndexerErrorInfo(err).message || 'بارگذاری نتایج ناموفق بود',
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    inspectComplete,
    inspectJobId,
    indexJobId,
    projectRef,
    applyInspectPayload,
    getProjectJob,
  ]);

  async function onSubmitIndex() {
    if (urlsToSend.length === 0) {
      await patchOnboarding({ projectId: projectRef, step: 5 }).unwrap();
      onComplete([]);
      return;
    }
    setBusy(true);
    setMessage(null);
    setProgressDone(false);
    setDoneJobId(null);
    completedRef.current = false;
    completionMetaRef.current = null;
    sentUrlsRef.current = urlsToSend;
    try {
      const queued = await enqueueIndex({
        projectId: projectRef,
        urls: urlsToSend,
        type: 'URL_UPDATED',
      }).unwrap();
      setMessage({
        variant: 'info',
        text: 'درخواست ایندکس به گوگل ارسال می‌شود. لطفاً صبر کنید…',
      });
      await patchOnboarding({
        projectId: projectRef,
        indexJobId: queued.jobId,
        step: 4,
      }).unwrap();
      setActiveJobId(queued.jobId);
    } catch (err) {
      sentUrlsRef.current = [];
      setMessage({
        variant: 'danger',
        text: getIndexerErrorInfo(err).message || 'ارسال ایندکس ناموفق بود',
      });
    } finally {
      setBusy(false);
    }
  }

  const continueJobId = doneJobId ?? activeJobId ?? job?.id ?? indexJobId;
  const showProgress = Boolean(job || activeJobId || indexJobId || progressDone);
  const socketActive = Boolean(
    activeJobId && !progressDone && (!job || !isJobDone(job.state)),
  );

  if (loading && !showProgress) {
    return (
      <Card className="mb-3 border">
        <Card.Body>
          <div className="d-flex align-items-center gap-2 py-2">
            <Spinner animation="border" size="sm" />
            <span>در حال بارگذاری نتایج بازرسی…</span>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (skipped && !showProgress) {
    return (
      <Card className="mb-3 border">
        <Card.Body>
          <Alert variant="success" className="mb-0">
            همه آدرس‌های بررسی‌شده ایندکس‌شده گزارش شدند. به مرحله زمان‌بندی
            می‌رویم.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-3 border">
      <Card.Header className="bg-body-tertiary">
        <h2 className="h5 mb-0">درخواست ایندکس</h2>
      </Card.Header>
      <Card.Body>
        {!showProgress && (
          <>
            <div className="d-flex justify-content-center mb-3">
              <div className="border rounded-pill bg-body-tertiary d-inline-flex align-items-stretch text-center">
                <div className="px-4 py-2 d-inline-flex align-items-center gap-2">
                  <span className="text-success fw-semibold fs-5">
                    {formatFaNumber(indexedCount)}
                  </span>
                  <SubtleBadge bg="success" pill className="fs--2">
                    ایندکس‌شده
                  </SubtleBadge>
                </div>
                <div className="vr my-2 opacity-25" aria-hidden />
                <div className="px-4 py-2 d-inline-flex align-items-center gap-2">
                  <span className="text-warning fw-semibold fs-5">
                    {formatFaNumber(notIndexedCount)}
                  </span>
                  <SubtleBadge bg="warning" pill className="fs--2">
                    ایندکس‌نشده
                  </SubtleBadge>
                </div>
              </div>
            </div>
            <UrlListTextarea
              value={urlListText}
              onChange={() => undefined}
              readOnly
              placeholder="آدرس ایندکس‌نشده‌ای یافت نشد."
            />
          </>
        )}

        {notIndexedUrls.length > INDEX_LIMIT && !showProgress && (
          <Alert variant="warning" className="small py-2 mt-3 mb-0">
            سقف روزانه {formatFaNumber(INDEX_LIMIT)} — فقط{' '}
            {formatFaNumber(INDEX_LIMIT)} اول ارسال می‌شود.
          </Alert>
        )}
        {message && (
          <Alert variant={message.variant} className="py-2 small mt-3 mb-0">
            {message.text}
          </Alert>
        )}

        {showProgress && (
          <div>
            {activeJobId && !progressDone && (
              <SocketProgressBar
                channelId={activeJobId}
                active={socketActive}
                description="در حال ارسال درخواست ایندکس…"
                onProgress={handleSocketProgress}
                onComplete={() => void finishIndexJob(activeJobId)}
              />
            )}
            {finishing && (
              <div className="d-flex align-items-center gap-2 mt-2 small text-secondary">
                <Spinner animation="border" size="sm" />
                در حال نهایی‌سازی نتایج…
              </div>
            )}
          </div>
        )}
      </Card.Body>
      {!showProgress && (
        <Card.Footer className="bg-body-tertiary border-top d-flex flex-wrap gap-2 justify-content-end py-3">
          {urlsToSend.length > 0 ? (
            <Button
              variant="falcon-primary"
              disabled={busy}
              onClick={() => void onSubmitIndex()}
            >
              {busy
                ? 'در حال شروع…'
                : `ارسال ${formatFaNumber(urlsToSend.length)} آدرس برای ایندکس`}
            </Button>
          ) : (
            <Button
              variant="outline-secondary"
              disabled={busy}
              onClick={() => {
                void patchOnboarding({ projectId: projectRef, step: 5 })
                  .unwrap()
                  .then(() => onComplete([]));
              }}
            >
              رد کردن
            </Button>
          )}
        </Card.Footer>
      )}
      {showProgress && progressDone && continueJobId && (
        <Card.Footer className="bg-body-tertiary border-top d-flex justify-content-end py-3">
          <Button
            variant="falcon-primary"
            disabled={finishing}
            onClick={() => void advanceToNext(continueJobId)}
          >
            {finishing ? 'در حال ادامه…' : 'ادامه'}
          </Button>
        </Card.Footer>
      )}
    </Card>
  );
}
